/**
 * CashPage — household cash drag analyzer
 *
 * Surfaces every £ of cash sitting in the household, splits it into
 * "touchable" (inside discretionary accounts — Simon can deploy)
 * vs "manager-controlled" (advisory only — Simon CANNOT touch this).
 *
 * Critical rule: managed cash recommendations are advisory only.
 */
import { useEffect, useState } from 'react'
import { Banknote, AlertTriangle, Lock, Unlock, TrendingDown, RefreshCw } from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '@/lib/api'
import PageHeader from '@/components/ui/PageHeader'

interface CashPosition {
  holding_id: number
  account_id: number
  account_name: string
  owner: string
  provider: string
  discretionary: boolean
  value_gbp: number
  is_premium_bonds: boolean
  current_yield_pct: number
  deployed_yield_pct: number
  drag_pct: number
  annual_drag_gbp: number
  annual_interest_now_gbp: number
  annual_interest_potential_gbp: number
}

interface ActionableRec {
  account_id: number
  account_name: string
  current_cash_gbp: number
  drag_pct: number
  annual_uplift_gbp: number
  recommendation: string
  suggested_vehicles: { ticker: string; name: string; yield_pct: number }[]
}

interface AdvisoryItem {
  account_id: number
  account_name: string
  owner: string
  current_cash_gbp: number
  drag_pct: number
  annual_uplift_gbp: number
  message: string
}

interface CashDragSummary {
  household_total_gbp: number
  total_cash_gbp: number
  cash_pct_of_household: number
  touchable_cash_gbp: number
  locked_cash_gbp: number
  total_annual_drag_gbp: number
  touchable_annual_drag_gbp: number
  locked_annual_drag_gbp: number
  grade: 'A' | 'B' | 'C' | 'D' | 'F'
  verdict: string
  positions: CashPosition[]
  actionable_recommendations: ActionableRec[]
  advisory_items: AdvisoryItem[]
  yield_assumptions: {
    money_market_gbp_pct: number
    premium_bonds_pct: number
    broker_cash_pct: Record<string, number>
  }
}

// Null-safe — API fields can always be null.
const fmt = (n: number | null | undefined) =>
  n == null ? '—' : `£${n.toLocaleString('en-GB', { maximumFractionDigits: 0 })}`

const fmtPct = (n: number | null | undefined) =>
  n == null ? '—' : `${n.toFixed(2)}%`

const GRADE_COLOURS: Record<string, string> = {
  A: 'text-emerald bg-emerald/10 border-emerald/30',
  B: 'text-emerald-light bg-emerald/10 border-emerald/20',
  C: 'text-amber bg-amber/10 border-amber/20',
  D: 'text-amber-light bg-amber/10 border-amber/30',
  F: 'text-rose-light bg-rose/10 border-rose/30',
}

export default function CashPage() {
  const [data, setData] = useState<CashDragSummary | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetch = async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await api.get<CashDragSummary>('/api/cash-drag/summary')
      setData(res.data)
    } catch (err: any) {
      setError(err?.message ?? 'Failed to load cash drag analysis')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetch()
  }, [])

  if (loading && !data) {
    return (
      <div className="space-y-4 animate-fade-in">
        <div className="h-8 w-64 bg-surface-card rounded animate-pulse" />
        <div className="h-32 bg-surface-card rounded-xl animate-pulse" />
        <div className="h-64 bg-surface-card rounded-xl animate-pulse" />
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="card p-6 text-center">
        <p className="text-rose-light">{error ?? 'No data'}</p>
        <button type="button" onClick={fetch} className="btn-secondary mt-3">
          Retry
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader
        title="Cash Drag Analyzer"
        icon={Banknote}
        subtitle="Where every £ of household cash is sitting, what it's earning, and what you're losing"
        actions={
          <button type="button" onClick={fetch} className="btn-secondary gap-2 text-xs">
            <RefreshCw size={13} aria-hidden />
            Refresh
          </button>
        }
      />

      {/* Scope disclaimer */}
      <div className="card p-3 bg-blue/5 border-blue/20 text-xs text-text-secondary flex items-start gap-2">
        <AlertTriangle size={13} className="text-blue-light shrink-0 mt-0.5" aria-hidden />
        <span>
          <span className="text-text-primary font-semibold">Scope:</span> tracked broker accounts only
          (AJ Bell, IG, HL, Coinbase, Premium Bonds). Cash in external high-street current accounts
          and savings accounts is not included.
        </span>
      </div>

      {/* Headline KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 stagger-children">
        <div className="stat-card">
          <p className="panel-title">Total Cash</p>
          <p className="stat-hero text-text-primary">{fmt(data.total_cash_gbp)}</p>
          <p className="text-xs text-text-muted">
            {data.cash_pct_of_household.toFixed(1)}% of household
          </p>
        </div>
        <div className="stat-card">
          <p className="stat-label flex items-center gap-1">
            <Unlock size={11} aria-hidden /> Touchable
          </p>
          <p className="stat-value text-emerald-light">{fmt(data.touchable_cash_gbp)}</p>
          <p className="text-xs text-text-muted">discretionary accounts</p>
        </div>
        <div className="stat-card">
          <p className="stat-label flex items-center gap-1">
            <Lock size={11} aria-hidden /> Manager-Controlled
          </p>
          <p className="stat-value text-text-secondary">{fmt(data.locked_cash_gbp)}</p>
          <p className="text-xs text-text-muted">advisory only</p>
        </div>
        <div className="stat-card">
          <p className="stat-label flex items-center gap-1">
            <TrendingDown size={11} aria-hidden /> Annual Drag
          </p>
          <p className="stat-value text-rose-light">{fmt(data.total_annual_drag_gbp)}/yr</p>
          <p className="text-xs text-text-muted">vs money-market rate</p>
        </div>
      </div>

      {/* Grade banner */}
      <div className={clsx('card p-4 border-2 flex items-center gap-4', GRADE_COLOURS[data.grade])}>
        <div className="text-4xl font-bold w-14 text-center">{data.grade}</div>
        <div className="flex-1">
          <p className="text-sm font-semibold text-text-primary">{data.verdict}</p>
          <p className="text-xs text-text-muted mt-1">
            Based on £{data.total_annual_drag_gbp.toLocaleString('en-GB', { maximumFractionDigits: 0 })}/yr
            of foregone interest at current money-market rates ({fmtPct(data.yield_assumptions.money_market_gbp_pct)}).
          </p>
        </div>
      </div>

      {/* Actionable recommendations (touchable cash) */}
      <section>
        <h2 className="panel-title mb-3 flex items-center gap-2">
          <Unlock size={14} className="text-emerald" aria-hidden />
          Actionable — Touchable Cash
          <span className="badge-green text-[10px]">You can move this</span>
        </h2>
        {data.actionable_recommendations.length === 0 ? (
          <div className="card p-4 text-sm text-text-muted">
            No actionable cash drag in your discretionary accounts. Touchable cash is either
            already deployed or below the £100 threshold.
          </div>
        ) : (
          <div className="space-y-3">
            {data.actionable_recommendations.map(rec => (
              <div key={rec.account_id} className="card p-4 border-emerald/20">
                <div className="flex items-start gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-text-primary">{rec.account_name}</p>
                    <p className="text-xs text-text-secondary mt-1">{rec.recommendation}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-lg font-bold text-emerald-light">
                      +{fmt(rec.annual_uplift_gbp)}/yr
                    </p>
                    <p className="text-[10px] text-text-muted">potential uplift</p>
                  </div>
                </div>
                <div className="mt-3 flex items-center gap-2 flex-wrap text-xs">
                  <span className="text-text-muted">Vehicles:</span>
                  {rec.suggested_vehicles.map(v => (
                    <span
                      key={v.ticker}
                      className="badge badge-blue text-[10px]"
                      title={v.name}
                    >
                      {v.ticker} · {fmtPct(v.yield_pct)}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Advisory items (manager-controlled cash) */}
      <section>
        <h2 className="panel-title mb-3 flex items-center gap-2">
          <Lock size={14} className="text-amber" aria-hidden />
          Advisory — Manager-Controlled Cash
          <span className="badge-neutral text-[10px]">View only</span>
        </h2>
        <div className="card p-3 mb-3 bg-amber/5 border-amber/20 text-xs text-text-secondary flex items-start gap-2">
          <AlertTriangle size={14} className="text-amber shrink-0 mt-0.5" aria-hidden />
          <span>
            You cannot directly action these. They live inside accounts where a fund manager
            or IFA controls deployment. Use these numbers to challenge the manager — not to trade.
          </span>
        </div>
        {data.advisory_items.length === 0 ? (
          <div className="card p-4 text-sm text-text-muted">
            No significant cash drag in manager-controlled accounts.
          </div>
        ) : (
          <div className="space-y-3">
            {data.advisory_items.map(adv => (
              <div key={adv.account_id} className="card p-4 border-amber/20">
                <div className="flex items-start gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-text-primary">
                      {adv.account_name} <span className="text-xs text-text-muted">· {adv.owner}</span>
                    </p>
                    <p className="text-xs text-text-secondary mt-1">{adv.message}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-lg font-bold text-amber-light">
                      −{fmt(adv.annual_uplift_gbp)}/yr
                    </p>
                    <p className="text-[10px] text-text-muted">foregone interest</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Cash positions table */}
      <section>
        <h2 className="panel-title mb-3">All Cash Positions</h2>
        <div className="card overflow-hidden">
          <div className="table-responsive">
            <table className="table-pro w-full text-xs">
              <thead>
                <tr>
                  <th className="px-4 py-3">Account</th>
                  <th className="px-2 py-3">Status</th>
                  <th className="!text-right px-2 py-3">Cash</th>
                  <th className="!text-right px-2 py-3">Earning now</th>
                  <th className="!text-right px-2 py-3">Could earn</th>
                  <th className="!text-right px-2 py-3">Cost of waiting/yr</th>
                </tr>
              </thead>
              <tbody>
                {data.positions.map(c => (
                  <tr key={c.holding_id}>
                    <td className="px-4 py-3">
                      <div className="text-text-primary font-medium">{c.account_name}</div>
                      <div className="text-[10px] text-text-muted">{c.owner}</div>
                    </td>
                    <td className="px-2 py-3">
                      {c.discretionary ? (
                        <span className="badge-green text-[10px]">Touchable</span>
                      ) : (
                        <span className="badge-neutral text-[10px]">Locked</span>
                      )}
                      {c.is_premium_bonds && (
                        <span className="badge-blue text-[10px] ml-1">Premium Bonds</span>
                      )}
                    </td>
                    <td className="text-right px-2 py-3 font-mono font-semibold text-text-primary">
                      {fmt(c.value_gbp)}
                    </td>
                    <td className="text-right px-2 py-3 font-mono text-text-secondary">
                      {fmtPct(c.current_yield_pct)}
                    </td>
                    <td className="text-right px-2 py-3 font-mono text-emerald-light">
                      {fmtPct(c.deployed_yield_pct)}
                    </td>
                    <td className="text-right px-2 py-3 font-mono">
                      {c.annual_drag_gbp > 0 ? (
                        <span className="delta-chip-neg">−{fmt(c.annual_drag_gbp)}</span>
                      ) : (
                        <span className="text-text-muted">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-surface-card border-t border-border">
                <tr>
                  <td colSpan={2} className="px-4 py-3 font-semibold text-text-primary">Total</td>
                  <td className="text-right px-2 py-3 font-mono font-bold text-text-primary">
                    {fmt(data.total_cash_gbp)}
                  </td>
                  <td colSpan={2} />
                  <td className="text-right px-2 py-3 font-mono font-bold text-rose-light">
                    −{fmt(data.total_annual_drag_gbp)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </section>

      {/* Methodology footer */}
      <details className="card p-4 text-xs text-text-secondary">
        <summary className="cursor-pointer text-text-primary font-semibold">
          Methodology &amp; assumptions
        </summary>
        <div className="mt-3 space-y-2">
          <p>
            <span className="text-text-muted">Money-market rate:</span>{' '}
            {fmtPct(data.yield_assumptions.money_market_gbp_pct)} (based on Royal London / iShares
            short-term cash funds, April 2026).
          </p>
          <p>
            <span className="text-text-muted">Premium Bonds equivalent:</span>{' '}
            {fmtPct(data.yield_assumptions.premium_bonds_pct)} prize fund rate (NS&amp;I).
          </p>
          <p>
            <span className="text-text-muted">Broker cash interest paid:</span>{' '}
            {Object.entries(data.yield_assumptions.broker_cash_pct)
              .map(([p, y]) => `${p} ${fmtPct(y as number)}`)
              .join(', ')}
            .
          </p>
          <p className="pt-2 text-text-muted">
            Drag = (potential yield − current yield) × cash balance. Touchable cash counts only
            against discretionary accounts you can actually trade. Manager-controlled cash is
            shown for advisory purposes — to challenge your IFA or fund manager — never as an
            actionable trade recommendation.
          </p>
        </div>
      </details>
    </div>
  )
}
